// Per-coverage-type form configuration for the Add Coverage dialog.
//
// Keys MUST match the canonical AMS `PolicyCoverages` shape in
// `shared/schema.ts` — that is the shape every read path uses
// (`buildCoverageLines`, COI generator, additional-insured + loss-payee
// pickers). The PUT route translates these to the DC ingest schema's
// alternate names (autoLiability → automobileLiability, etc.) inside
// `server/services/coverageTranslator.ts` before forwarding.
import type { PolicyCoverages } from "@shared/schema";

export type CoverageFieldType = "currency" | "select" | "checkbox" | "text";

export interface CoverageFormField {
  key: string;
  label: string;
  type: CoverageFieldType;
  options?: { value: string; label: string }[];
  required?: boolean;
}

export interface CoverageFormConfig {
  key: keyof PolicyCoverages;
  label: string;
  fields: CoverageFormField[];
}

const GL_FIELDS: CoverageFormField[] = [
  { key: "eachOccurrence", label: "Each Occurrence", type: "currency", required: true },
  { key: "damageToRented", label: "Damage to Rented Premises", type: "currency" },
  { key: "medExp", label: "Med Exp (Any one person)", type: "currency" },
  { key: "personalAdvInjury", label: "Personal & Adv Injury", type: "currency" },
  { key: "generalAggregate", label: "General Aggregate", type: "currency", required: true },
  { key: "productsCompOpAgg", label: "Products-Comp/Op Agg", type: "currency" },
  {
    key: "claimsMade",
    label: "Policy Type",
    type: "select",
    options: [
      { value: "false", label: "Occurrence" },
      { value: "true", label: "Claims Made" },
    ],
  },
  {
    key: "aggregateAppliesPer",
    label: "Aggregate Applies Per",
    type: "select",
    options: [
      { value: "policy", label: "Policy" },
      { value: "project", label: "Project" },
      { value: "loc", label: "Location" },
    ],
  },
];

const AUTO_FIELDS: CoverageFormField[] = [
  // CSR-1248: CSL is not required — split-limit policies use BI per person /
  // BI per accident / PD instead. At least one AL limit field must be filled
  // (validated in AddCoverageDialog canSubmit).
  { key: "combinedSingleLimit", label: "Combined Single Limit", type: "currency" },
  { key: "bodilyInjuryPerPerson", label: "Bodily Injury (Per Person)", type: "currency" },
  { key: "bodilyInjuryPerAccident", label: "Bodily Injury (Per Accident)", type: "currency" },
  { key: "propertyDamage", label: "Property Damage", type: "currency" },
  {
    key: "autoType",
    label: "Auto Type",
    type: "select",
    options: [
      { value: "any", label: "Any Auto" },
      { value: "all_owned", label: "All Owned" },
      { value: "scheduled", label: "Scheduled" },
      { value: "hired", label: "Hired" },
      { value: "non_owned", label: "Non-Owned" },
    ],
    required: true,
  },
  { key: "uninsuredMotoristCsl", label: "Uninsured Motorist", type: "currency" },
  { key: "underinsuredMotoristBi", label: "Underinsured Motorist", type: "currency" },
  { key: "medicalPayments", label: "Medical Payments", type: "currency" },
  { key: "basicPip", label: "Basic PIP", type: "currency" },
  { key: "pip", label: "PIP", type: "currency" },
];

export const COVERAGE_FORM_CONFIGS: CoverageFormConfig[] = [
  { key: "generalLiability", label: "General Liability", fields: GL_FIELDS },
  { key: "autoLiability", label: "Auto Liability", fields: AUTO_FIELDS },
  {
    key: "umbrella",
    label: "Umbrella / Excess Liability",
    fields: [
      { key: "eachOccurrence", label: "Each Occurrence", type: "currency", required: true },
      { key: "aggregate", label: "Aggregate", type: "currency" },
      { key: "deductible", label: "Deductible", type: "currency" },
      { key: "isExcess", label: "Excess (vs Umbrella)", type: "checkbox" },
    ],
  },
  {
    key: "workersComp",
    label: "Workers Compensation",
    fields: [
      { key: "elEachAccident", label: "E.L. Each Accident", type: "currency", required: true },
      { key: "elDiseaseEaEmployee", label: "E.L. Disease — Each Employee", type: "currency" },
      { key: "elDiseasePolicyLimit", label: "E.L. Disease — Policy Limit", type: "currency" },
      { key: "perStatute", label: "Per Statute", type: "checkbox" },
    ],
  },
  {
    key: "cargo",
    label: "Motor Truck Cargo",
    fields: [
      { key: "limit", label: "Limit", type: "currency", required: true },
      { key: "deductible", label: "Deductible", type: "currency" },
      // CSR-1566: flags Reefer Breakdown on the MTC line; the COI appends
      // "/Reefer Breakdown" to the label using the same limit/deductible.
      { key: "reeferBreakdown", label: "Reefer Breakdown", type: "checkbox" },
    ],
  },
  {
    key: "physicalDamage",
    label: "Physical Damage",
    fields: [
      { key: "comprehensiveDeductible", label: "Comprehensive Deductible", type: "currency" },
      { key: "collisionDeductible", label: "Collision Deductible", type: "currency" },
    ],
  },
  {
    key: "onHook",
    label: "On-Hook",
    fields: [
      { key: "limit", label: "Limit", type: "currency", required: true },
      { key: "deductible", label: "Deductible", type: "currency" },
    ],
  },
  {
    key: "trailerInterchange",
    label: "Trailer Interchange",
    fields: [
      { key: "limit", label: "Limit", type: "currency", required: true },
      { key: "deductible", label: "Deductible", type: "currency" },
    ],
  },
  {
    key: "nonOwnedTrailerPhysicalDamage",
    label: "Non-Owned Trailer Physical Damage",
    fields: [
      { key: "limit", label: "Limit", type: "currency", required: true },
      { key: "deductible", label: "Deductible", type: "currency" },
    ],
  },
  {
    key: "hiredAutoLiability",
    label: "Hired Auto Liability",
    fields: [{ key: "combinedSingleLimit", label: "Combined Single Limit", type: "currency", required: true }],
  },
  {
    key: "hiredNonOwnedLiability",
    label: "Hired & Non-Owned Auto Liability",
    fields: [{ key: "limit", label: "Limit", type: "currency", required: true }],
  },
  {
    key: "nonTruckingLiability",
    label: "Non-Trucking Liability",
    fields: [{ key: "limit", label: "Limit", type: "currency", required: true }],
  },
  {
    key: "nonOwnedAuto",
    label: "Non-Owned Auto",
    fields: [{ key: "limit", label: "Limit", type: "currency", required: true }],
  },
  {
    key: "rentalReimbursement",
    label: "Rental Reimbursement",
    fields: [
      { key: "perDay", label: "Per Day", type: "currency", required: true },
      { key: "maxPayout", label: "Max Payout", type: "currency" },
      { key: "appliesTo", label: "Applies To", type: "text" },
    ],
  },
];

export function getCoverageConfig(key: string): CoverageFormConfig | undefined {
  return COVERAGE_FORM_CONFIGS.find((c) => c.key === key);
}
